import { put, putResolve, select } from "redux-saga/effects";
import * as H from "history";
import { TripCategory, CallState, TripDetails, FlightBookType } from "redmond";
import { CfarOfferType } from "redmond/apis/tysons/ancillaries";
import { actions } from "../../actions";
import { fetchTripDetails as fetchDetails } from "../../../../api/v0/shop/fetchTripDetails";
import { actions as shopActions } from "../../../shop/actions";
import {
  disruptionProtectionOfferSelector,
  fetchAncillaryOfferCallStateSelector,
} from "../../../shop/reducer/selectors";
import { hasActiveRefundableFareInFlightBookSelector } from "../../reducer/selectors";
import {
  getExistingStateVariables,
  IFlightBookParsedQuery,
  IMulticityFlightBookParsedQuery,
  parseQueryString,
} from "../populateBookQueryParamsSaga";
import Logger from "../../../../helpers/Logger";
import { IStoreState } from "../../../../reducers/types";
import { PATH_HOME } from "../../../../utils/urlPaths";
import { ISelectedMulticityTrip } from "../../../shop/reducer";

const isMulticity = (tripCategory: TripCategory) =>
  tripCategory === TripCategory.MULTI_CITY;

const shouldRedirect = ({
  outgoingFareId,
  tripCategory,
  returnFareId,
  tripId,
  tripDetails,
  multicityDepartureFareIds,
  selectedMulticityTrips,
}: {
  tripId: string;
  outgoingFareId?: string;
  returnFareId?: string;
  multicityDepartureFareIds?: string[];
  selectedMulticityTrips?: ISelectedMulticityTrip[];
  tripCategory: TripCategory;
  tripDetails: TripDetails;
}) => {
  if (isMulticity(tripCategory)) {
    const numSearchedRoutes = selectedMulticityTrips?.length;
    const numDepartureFareIds = multicityDepartureFareIds?.length;
    return !tripDetails || !tripId || numSearchedRoutes !== numDepartureFareIds;
  }
  return (
    !tripDetails ||
    !outgoingFareId ||
    !tripId ||
    (tripCategory === TripCategory.ROUND_TRIP && !returnFareId)
  );
};
export function* setUpFlightBookParamsSaga({
  history,
  flightBookType,
}: actions.ISetupFlightBookParams) {
  try {
    const state: IStoreState = yield select();

    let { tripDetails, tripCategory, selectedMulticityTrips, outgoingSliceId } =
      getExistingStateVariables(state);

    if (isMulticity(tripCategory)) {
      const parsedMcQueryString = parseQueryString(
        history
      ) as IMulticityFlightBookParsedQuery;

      const {
        tripId,
        tripCategory: currentTripCategory,
        departureFareId0,
        departureFareId1,
        departureFareId2,
        departureFareId3,
        departureFareId4,
        disruptionProtectionPolicyId,
        disruptionProtectionProductId,
      } = parsedMcQueryString;

      const multicityDepartureFareIds = [
        departureFareId0,
        departureFareId1,
        departureFareId2 || "",
        departureFareId3 || "",
        departureFareId4 || "",
      ].filter(Boolean);

      if (tripId) {
        yield putResolve(actions.setFlightBookType(FlightBookType.DEFAULT));

        if (!tripDetails || !tripCategory || tripDetails.id != tripId) {
          tripDetails = yield fetchDetails(tripId);
        }
      }

      yield setUpTripDetails({
        tripId,
        multicityDepartureFareIds,
        selectedMulticityTrips,
        tripCategory: currentTripCategory,
        tripDetails,
        history,
      });

      // note: handle ancillaries (only disruption for multi-city) after tripDetails are set
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default: {
          if (disruptionProtectionPolicyId && disruptionProtectionProductId) {
            const disruptionProtectionOffer =
              disruptionProtectionOfferSelector(state);
            const hasActiveRefundableFare =
              hasActiveRefundableFareInFlightBookSelector(state);

            if (
              !disruptionProtectionOffer ||
              disruptionProtectionOffer.length === 0
            ) {
              yield putResolve(
                shopActions.fetchAncillaryOffer({
                  preserveCfarId: hasActiveRefundableFare,
                })
              );
            }

            yield putResolve(
              shopActions.setSelectedDisruptionProtectionId({
                policyId: disruptionProtectionPolicyId,
                productId: disruptionProtectionProductId,
              })
            );
          }
          break;
        }
      }
    } else {
      const parsedOWRTQueryString = parseQueryString(
        history
      ) as IFlightBookParsedQuery;

      let {
        tripId,
        outgoingFareId,
        returnFareId,
        tripCategory: currentTripCategory,
        priceDropCandidateId,
        cfarPolicyId,
        cfarQuoteId,
        cfarProductId,
        cfarChoice,
        cfarFareChoice,
        cfarRefundAmount,
        chfarQuoteId,
        isRefundableFare: hasSelectedRefundableFare,
        disruptionProtectionPolicyId,
        disruptionProtectionProductId,
        disruptionProtectionQuoteId,
        disruptionOfferType,
        cfarOfferType,
      } = parsedOWRTQueryString;

      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default: {
          if (tripId) {
            yield putResolve(actions.setFlightBookType(FlightBookType.DEFAULT));
            yield put(
              shopActions.updateRefundableFaresProperties({
                cfar_choice: cfarChoice,
                cfar_fare_choice: cfarFareChoice,
                cfar_refund_amount:
                  cfarRefundAmount != null
                    ? Number(cfarRefundAmount)
                    : undefined,
              })
            );

            if (!tripDetails || !tripCategory || tripDetails.id != tripId) {
              tripDetails = yield fetchDetails(tripId);
            }
            if (priceDropCandidateId) {
              yield put(
                actions.fetchPriceDropEligibility(priceDropCandidateId)
              );
            }
          }
          break;
        }
      }

      yield setUpTripDetails({
        tripId,
        outgoingFareId,
        outgoingSliceId: outgoingSliceId || undefined,
        returnFareId,
        tripCategory: currentTripCategory,
        tripDetails,
        history,
      });

      // note: handle ancillaries after tripDetails are set
      switch (flightBookType) {
        case FlightBookType.CHANGE_FOR_ANY_REASON_EXERCISE:
        case FlightBookType.DEFAULT:
        default: {
          const fetchAncillaryOfferCallState =
            fetchAncillaryOfferCallStateSelector(state);
          const hasActiveRefundableFare =
            hasActiveRefundableFareInFlightBookSelector(state);

          yield* fetchAncillariesIfNececery({
            hasActiveRefundableFare,
            fetchAncillaryOfferCallState,
            disruptionProtectionPolicyId,
            cfarPolicyId,
            chfarQuoteId,
            flightBookType,
            disruptionProtectionProductId,
            disruptionProtectionQuoteId,
            disruptionOfferType,
            cfarProductId,
            cfarQuoteId,
            hasSelectedRefundableFare,
            cfarOfferType,
          });
          break;
        }
      }
    }
  } catch (e) {
    Logger.debug(e);
    yield put(actions.setUpFlightBookParamsFailed());
  }
}

function* fetchAncillariesIfNececery(args: {
  hasActiveRefundableFare: boolean;
  fetchAncillaryOfferCallState: CallState;
  disruptionProtectionPolicyId?: string;
  cfarPolicyId?: string;
  chfarQuoteId?: string;
  flightBookType?: FlightBookType;
  disruptionProtectionProductId?: string;
  disruptionProtectionQuoteId?: string;
  disruptionOfferType?: string;
  cfarProductId?: string;
  cfarQuoteId?: string;
  hasSelectedRefundableFare?: boolean;
  cfarOfferType?: "hts" | "legacy";
}) {
  const {
    hasActiveRefundableFare,
    disruptionProtectionPolicyId,
    fetchAncillaryOfferCallState,
    cfarPolicyId,
    chfarQuoteId,
    flightBookType,
    disruptionProtectionProductId,
    disruptionProtectionQuoteId,
    cfarProductId,
    cfarQuoteId,
    hasSelectedRefundableFare,
    cfarOfferType,
  } = args;
  if (!disruptionProtectionPolicyId && !cfarPolicyId && !chfarQuoteId) {
    return;
  }

  if (fetchAncillaryOfferCallState != CallState.NotCalled) {
    return;
  }

  yield putResolve(
    shopActions.fetchAncillaryOffer({
      preserveCfarId: hasActiveRefundableFare,
    })
  );

  if (disruptionProtectionPolicyId && disruptionProtectionProductId) {
    yield putResolve(
      shopActions.setSelectedDisruptionProtectionId({
        policyId: disruptionProtectionPolicyId,
        productId: disruptionProtectionProductId,
        quoteId: disruptionProtectionQuoteId,
      })
    );
  }

  if (cfarPolicyId && cfarProductId) {
    yield putResolve(
      shopActions.setSelectedCfarId({
        policyId: cfarPolicyId,
        productId: cfarProductId,
        quoteId: cfarQuoteId,
        offerType: cfarOfferType === "hts" ? CfarOfferType.Hts : CfarOfferType.Legacy,
      })
    );

    if (
      hasSelectedRefundableFare &&
      (flightBookType === FlightBookType.DEFAULT ||
        flightBookType === undefined)
    ) {
      yield putResolve(shopActions.setHasSelectedRefundableFare(true));
    }
  }

  if (chfarQuoteId) {
    yield putResolve(
      shopActions.setSelectedChfarId({
        quoteId: chfarQuoteId,
      })
    );
  }
}

function* setUpTripDetails(args: {
  tripId: string;
  outgoingFareId?: string;
  outgoingSliceId?: string;
  returnFareId?: string;
  multicityDepartureFareIds?: string[];
  selectedMulticityTrips?: ISelectedMulticityTrip[];
  tripCategory: TripCategory;
  tripDetails: TripDetails;
  history: H.History;
}) {
  const {
    tripId,
    outgoingFareId,
    outgoingSliceId,
    returnFareId,
    multicityDepartureFareIds,
    selectedMulticityTrips,
    tripCategory,
    tripDetails,
    history,
  } = args;

  // If we are missing the data we need from both the state and the query params
  // we should redirect the user home.
  if (
    shouldRedirect({
      tripId,
      outgoingFareId,
      returnFareId,
      multicityDepartureFareIds,
      selectedMulticityTrips,
      tripCategory,
      tripDetails,
    })
  ) {
    return history.push(PATH_HOME);
  }

  // Trip Details must be populated last as the UI waits to display until trip is set
  if (!isMulticity(tripCategory)) {
    if (!outgoingSliceId) {
      yield putResolve(
        shopActions.setChosenOutgoingSlice({
          outgoingSliceId: tripDetails.slices[0].id,
          outgoingFareId: outgoingFareId!,
          outgoingFareRating: undefined,
          tripId,
        })
      );
    }
    if (tripCategory === TripCategory.ROUND_TRIP) {
      yield putResolve(
        shopActions.setChosenReturnSlice({
          returnSliceId: tripDetails.slices[1].id,
          returnFareId: returnFareId!,
          returnFareRating: tripDetails.fareDetails.find(
            (f) => f.id === returnFareId
          )?.slices[1].fareShelf?.rating,
          tripId,
        })
      );
    }
  }

  yield put(shopActions.setTripDetails(tripDetails));
  yield put(actions.setUpFlightBookParamsSuccess());
}
